import type { Project } from '@/types'

import { useRef } from 'react'

import { gsap, registerGsap, prefersReducedMotion } from '@/lib/gsap'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'
import { Reveal, FadeIn } from '@/components/animation/reveal'
import { ProjectGrid } from '@/components/projects/project-grid'

interface IntroProps {
    projects: Project[]
}

export function Intro({ projects }: IntroProps) {
    const sectionRef = useRef<HTMLDivElement>(null)

    // a-72 "Change Section Color": the section starts dark (#100f10 / white text)
    // and recolors to white / black text as the "MY WORK" title scrolls into view.
    useIsomorphicLayoutEffect(() => {
        const el = sectionRef.current
        if (!el || prefersReducedMotion()) return
        registerGsap()

        const ctx = gsap.context(() => {
            const trigger = el.querySelector('.prework-title') ?? el
            gsap.to(el, {
                backgroundColor: '#fff',
                color: '#000',
                ease: 'none',
                scrollTrigger: {
                    trigger,
                    start: 'top 85%',
                    end: 'top 40%',
                    scrub: true
                }
            })
        }, sectionRef)

        return () => ctx.revert()
    }, [])

    return (
        <div ref={sectionRef} className='section dark-section'>
            <div className='container'>
                <div className='w-layout-grid pre-work-grid'>
                    <div
                        id='w-node-_741da83a-78d3-18d9-8e12-bc788baec05e-78095375'
                        className='content'
                    >
                        <div className='content'>
                            <Reveal mode='view' delay={0} innerClassName='perspective-load1-on-view'>
                                <div className='big-text'>Crafting motion that</div>
                            </Reveal>
                            <Reveal mode='view' delay={0.1} innerClassName='perspective-load2-on-view'>
                                <div className='big-text'>dictates how brands</div>
                            </Reveal>
                            <Reveal mode='view' delay={0.2} innerClassName='perspective-load3-on-view'>
                                <div className='big-text'>own their vibe across</div>
                            </Reveal>
                            <Reveal mode='view' delay={0.3} innerClassName='perspective-load3-on-view'>
                                <div className='big-text'>today&apos;s media.</div>
                            </Reveal>
                        </div>
                    </div>
                    <div
                        id='w-node-d728dff9-af34-8776-b740-b587b3c383f8-78095375'
                        className='content'
                    >
                        <div className='pre-work-description'>
                            <div className='heading-container'>
                                <div className='perspective large-perspective'>
                                    <FadeIn className='fade-in-slow-on-view'>
                                        <div>
                                            <strong>
                                                Turning brands into vibrant experiences that
                                                captivate.
                                            </strong>
                                            <br />
                                            <br />
                                            As the media landscape undergoes a rapid transformation,
                                            brands must evolve their communication to thrive in this
                                            dynamic era. Motion emerges as the key link between a
                                            brand and its audience, a fundamental aspect that
                                            reshapes a brand&apos;s platform and communication
                                            approach in innovative ways.
                                            <br />
                                            <br />
                                            Partnering with trailblazing brand leaders, we design
                                            compelling motion identities, systems, and applications
                                            that intensify audience connections, reinforce brand
                                            affinity, and propel sustained growth into the future.
                                        </div>
                                    </FadeIn>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='prework-title'>
                    <div className='hero-hello-text'>
                        <div className='perspective'>
                            <FadeIn className='fade-in-slow-on-view'>
                                <div className='hero-text-10pct-opacity'>
                                    <div className='home-hero-text with-opacity-10pct'>
                                        MY WORK
                                    </div>
                                </div>
                            </FadeIn>
                        </div>
                    </div>
                </div>

                <ProjectGrid projects={projects} overlayWhite />
            </div>
        </div>
    )
}
